// src/Services/msalClient.ts
import { PublicClientApplication, AccountInfo, EventType } from '@azure/msal-browser';
import adconfig from '../ADConfig.json';

export const API_SCOPE = adconfig.apiScope;

export const msalInstance = new PublicClientApplication({
  auth: {
    clientId: adconfig.clientId, // SPA app id
    authority: `https://login.microsoftonline.com/${adconfig.tenant}`,
    redirectUri: window.location.origin,
  },
  cache: { cacheLocation: 'localStorage' },
});

// Initialize ONCE and export a Promise everyone can await.
export const msalReady = (async () => {
  await msalInstance.initialize();

  // 1) Use the selected account returned from the redirect
  const redirectResult = await msalInstance.handleRedirectPromise();
  if (redirectResult?.account) {
    msalInstance.setActiveAccount(redirectResult.account);
  } else {
    // Only auto-pick if there is exactly ONE cached account.
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 1) {
      msalInstance.setActiveAccount(accounts[0]);
    }
    // If there are multiple accounts, leave active account unset until user chooses.
  }
})();

// Single, guarded entry point for interactive sign-in. Every caller that needs
// to send the user to the Microsoft login (no account, silent renewal failed,
// switching account) funnels through here, so we never fire two concurrent
// loginRedirect calls — MSAL throws `interaction_in_progress` if we do.
let interactiveSignInInFlight = false;

export async function signIn(): Promise<void> {
  await msalReady;
  if (interactiveSignInInFlight) return;
  interactiveSignInInFlight = true;
  try {
    await msalInstance.loginRedirect({ scopes: [API_SCOPE], prompt: 'select_account' });
    // loginRedirect navigates away; the flag stays set until the page reloads.
  } catch (error) {
    // Redirect didn't start (e.g. interaction already in progress) — allow a retry.
    interactiveSignInInFlight = false;
    throw error;
  }
}

export async function getToken(): Promise<string> {
  await msalReady;
  let account: AccountInfo | null = msalInstance.getActiveAccount();
  if (!account) {
    const all = msalInstance.getAllAccounts();
    if (all.length === 1) {
      // Exactly one cached account: unambiguous, so adopt it.
      account = all[0];
      msalInstance.setActiveAccount(account);
    }
    // With 0 or multiple accounts and none active there is no unambiguous
    // choice — fall through to interactive sign-in with the account chooser
    // rather than silently guessing (which could mint a token for the wrong user).
  }
  if (!account) {
    await signIn();
    return '';
  }
  try {
    const res = await msalInstance.acquireTokenSilent({ account, scopes: [API_SCOPE] });
    return res.accessToken;
  } catch (e) {
    // Silent renewal failed (no active AAD session, third-party cookies blocked, or the
    // session expired). MSAL attempts the interactive step inside a hidden, sandboxed iframe
    // which can't navigate the top window, so it hangs with monitor_window_timeout. Fall back
    // to a full-page interactive redirect so the Microsoft login panel actually appears.
    await signIn();
    return '';
  }
}

msalInstance.addEventCallback((e) => {
  if (e.eventType === EventType.LOGIN_SUCCESS || e.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
    const account = (e as any).payload?.account;
    if (account) {
      msalInstance.setActiveAccount(account);
    }
  }
});

export async function logout() {
  await msalReady;
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  await msalInstance.logoutRedirect({
    account,
    postLogoutRedirectUri: window.location.origin,
  });
}

/*
//You can use this to debug MSAL issues

// DEV ONLY: expose helpers for console debugging
if (process.env.NODE_ENV !== "production") {
  // @ts-ignore
  window.msalInstance = msalInstance;
  // @ts-ignore
  window.msalReady = msalReady;
  // @ts-ignore
  window.getToken = getToken;
  // @ts-ignore
  window.API_SCOPE = API_SCOPE;
}

//Use the following in dev tools console to clear the local storage while testing login issues
//localStorage.clear(); sessionStorage.clear();

//Use the following in dev tools console to analize the token
//await msalReady;
//const t = await getToken();
//const p = JSON.parse(atob(t.split('.')[1]));
//console.log(p);

*/
